/**
 * Screen-Reader Preview slide-over panel.
 * Linearizes the cards-pane into screen-reader-style announcements with
 * optional Web Speech API playback. Slide-over docks against the right
 * edge of the cards pane (NOT full window).
 *
 * Public API:
 *   buildTranscript(cards)           — pure: Card[] → TranscriptLine[]
 *   speakableTextForLine(line, mode) — pure: line → text-to-speak (skim/full)
 *   skimTrim(text)                   — pure: trim long prose to first sentence/10 words
 *   pickMaleVoice(voices)            — pure: voice list → preferred male English voice
 *   ScreenReaderPanel                — DOM-mounting controller (browser-only)
 *
 * Pure helpers run in node tests; the controller is browser-only.
 *
 * @module screen-reader
 */
import { ROLE_UNDEFINED } from "./role-constants.js";
import { normalizeSpeakableText, buildSrCloseButton } from "../a11y-view/sr-shared.js";
import { FocusTrap } from "../a11y-view/focus-trap.js";
// ─── Pure helpers (node-testable) ────────────────────────────────────────────
const SKIMMABLE_ROLES = Object.freeze(new Set(["Paragraph", "P", "Note", "Caption", "ListItem", "LI"]));
const DESC_ROLES = Object.freeze(new Set(["Figure", "Audio", "Video", "Formula", "Table"]));
/**
 * Convert a flat Card array from the cards pane into a linearised transcript.
 *
 * Behaviour contract (pinned by DDT):
 *   - Non-array input (null, undefined, object) → returns `[]` (defensive).
 *   - Empty array → returns `[]`.
 *   - Delegates each card to `transcriptLineFor`; null returns are dropped.
 *   - Order is preserved; followingCards slice is passed so List can count
 *     consecutive LI siblings when `listItemCount` is absent.
 *
 * @param cards - Flat card list from the cards pane (may be empty or non-array).
 * @returns Linearised transcript lines in document order.
 */
export function buildTranscript(cards) {
    if (!Array.isArray(cards))
        return [];
    const out = [];
    for (let i = 0; i < cards.length; i++) {
        const c = cards[i];
        if (!c)
            continue;
        const line = transcriptLineFor(c, cards.slice(i + 1));
        if (line)
            out.push(line);
    }
    return out;
}
/**
 * Produce a single `TranscriptLine` for one card, or `null` if the card is
 * falsy.
 *
 * Role-dispatch table (pinned by DDT):
 *   - `UNDEFINED` or `card.undefined === true` → kind=`"undef"`, text="Untagged content — type required", editable=false.
 *   - `H1`–`H6` → preamble="Heading level N", kind=`"normal"`, skimmable=false.
 *   - `Paragraph` / `P` → preamble="", kind=`"normal"`, skimmable=true.
 *   - `Caption` → preamble="Caption", kind=`"normal"`, skimmable=true.
 *   - `Note` → preamble="Footnote", kind=`"normal"`, skimmable=true.
 *   - `Span` → preamble="", kind=`"normal"`, skimmable=false.
 *   - `LI` / `ListItem` → preamble="", kind=`"li"`, skimmable=true.
 *   - `L` / `List` → text="List with N items" (N from `listItemCount` or count of
 *     consecutive `LI` / `ListItem` in `followingCards`), kind=`"normal"`, skimmable=false.
 *   - `Link` → preamble="Link", text=`${text} — ${alt}` when alt present, skimmable=false.
 *   - `Figure` with alt → preamble="Image", kind=`"normal"`, editable=true, isPlaceholder=false.
 *   - `Figure` without alt → kind=`"empty"`, isPlaceholder=true.
 *   - `Audio` / `Video` / `Formula` → same placeholder pattern as Figure with their
 *     own preamble labels ("Audio clip", "Video clip", "Formula").
 *   - `Table` with summary/alt → preamble="Table with R rows and C columns.", text=" summary", kind=`"normal"`, editable=true.
 *   - `Table` without summary → kind=`"empty"`, isPlaceholder=true, text=" no summary".
 *   - `Artifact` → kind=`"muted"`, text="(decorative — skipped by screen readers)".
 *   - Unknown role → preamble=role, kind=`"normal"`, skimmable=false (fallthrough).
 *
 * @param card - The card to convert.
 * @param followingCards - Remaining cards after this one, used for List item counting.
 * @returns A `TranscriptLine` or `null` for a falsy card.
 */
export function transcriptLineFor(card, followingCards) {
    if (!card)
        return null;
    const role = (card.role ?? card.roleToken) ?? "";
    if (role === ROLE_UNDEFINED || card.undefined === true) {
        return {
            structId: card.structId ?? "",
            role: ROLE_UNDEFINED,
            preamble: "",
            text: "Untagged content — type required",
            kind: "undef",
            editable: false,
            isPlaceholder: false,
            skimmable: false,
        };
    }
    // Migrated to the SHARED normalizer (rule #11 / A.7 — the SAME helper the O365
    // a11y-view SR popout consumes): collapses embedded whitespace runs, a superset of
    // the former bare `.trim()` (leading/trailing only). A screen reader speaks a card
    // as one utterance, so internal newlines should not survive into the transcript.
    const text = normalizeSpeakableText(card.text);
    const alt = normalizeSpeakableText(card.alt);
    const hMatch = String(role).match(/^H([1-6])$/);
    if (hMatch) {
        return {
            structId: card.structId ?? "",
            role,
            preamble: `Heading level ${hMatch[1]}`,
            text,
            kind: "normal",
            editable: false,
            isPlaceholder: false,
            skimmable: false,
        };
    }
    if (role === "Paragraph" || role === "P") {
        return mk(card, role, "", text, "normal", false, false, true);
    }
    if (role === "Caption") {
        return mk(card, role, "Caption", text, "normal", false, false, true);
    }
    if (role === "Note") {
        return mk(card, role, "Footnote", text, "normal", false, false, true);
    }
    if (role === "Span") {
        return mk(card, role, "", text, "normal", false, false, false);
    }
    if (role === "LI" || role === "ListItem") {
        return mk(card, role, "", text, "li", false, false, true);
    }
    if (role === "L" || role === "List") {
        const explicit = Number.isFinite(card.listItemCount) ? card.listItemCount : null;
        let n = explicit ?? null;
        if (n == null) {
            n = 0;
            const tail = Array.isArray(followingCards) ? followingCards : [];
            for (const fc of tail) {
                const r = (fc && (fc.role ?? fc.roleToken)) ?? "";
                if (r === "LI" || r === "ListItem")
                    n++;
                else
                    break;
            }
        }
        return mk(card, role, "", `List with ${n} items`, "normal", false, false, false);
    }
    if (role === "Link") {
        const desc = alt;
        return mk(card, role, "Link", text + (desc ? ` — ${desc}` : ""), "normal", false, false, false);
    }
    if (role === "Figure") {
        if (alt)
            return mk(card, role, "Image", alt, "normal", true, false, false);
        return mk(card, role, "Image", "no description", "empty", true, true, false);
    }
    if (role === "Audio") {
        if (alt)
            return mk(card, role, "Audio clip", alt, "normal", true, false, false);
        return mk(card, role, "Audio clip", "no description", "empty", true, true, false);
    }
    if (role === "Video") {
        if (alt)
            return mk(card, role, "Video clip", alt, "normal", true, false, false);
        return mk(card, role, "Video clip", "no description", "empty", true, true, false);
    }
    if (role === "Formula") {
        if (alt)
            return mk(card, role, "Formula", alt, "normal", true, false, false);
        return mk(card, role, "Formula", "no description", "empty", true, true, false);
    }
    if (role === "Table") {
        const rows = Number.isFinite(card.tableRows) ? card.tableRows : null;
        const cols = Number.isFinite(card.tableCols) ? card.tableCols : null;
        const dim = (rows != null && cols != null)
            ? `${rows} rows and ${cols} columns`
            : "rows and columns";
        const summary = ((card.tableSummary ?? alt) || "").trim();
        if (summary) {
            return mk(card, role, `Table with ${dim}.`, ` ${summary}`, "normal", true, false, false);
        }
        return mk(card, role, `Table with ${dim}.`, " no summary", "empty", true, true, false);
    }
    if (role === "Artifact") {
        return mk(card, role, "", "(decorative — skipped by screen readers)", "muted", false, false, false);
    }
    return mk(card, role, role, text, "normal", false, false, false);
}
function mk(card, role, preamble, text, kind, editable, isPlaceholder, skimmable) {
    return {
        structId: card.structId ?? "",
        role,
        preamble,
        text: String(text || ""),
        kind,
        editable: !!editable,
        isPlaceholder: !!isPlaceholder,
        skimmable: !!skimmable,
    };
}
/**
 * Trim long prose to the shorter of: first complete sentence (up to 300 chars)
 * or first 10 words.  Used by `speakableTextForLine` in skim mode.
 *
 * Behaviour contract (pinned by DDT):
 *   - Falsy input (empty string, null-ish) → returned unchanged (early return).
 *   - Text whose entire content fits within 10 words AND has no sentence
 *     boundary → returned unchanged (no ellipsis appended).
 *   - Text with a short first sentence (< 10-word length) → `firstSentence + "…"`.
 *   - Text with >10 words and no sentence boundary → first 10 words + `"…"`.
 *   - Candidate selection rule: `min(firstSentence.length, firstTenWords.length)`;
 *     when candidate equals the full text, no ellipsis is appended.
 *
 * @param text - The prose string to trim.
 * @returns The trimmed string, possibly with a trailing `"…"`.
 */
export function skimTrim(text) {
    if (!text)
        return text;
    const t = String(text);
    const sentMatch = t.match(/^(.{1,300}?[.!?])(\s|$)/);
    const firstSentence = sentMatch ? sentMatch[1] : t; // WHY: regex ^(.{1,300}?[.!?])(\s|$) guarantees capture group 1
    const words = t.split(/\s+/).filter(Boolean);
    const firstTenWords = words.slice(0, 10).join(" ");
    const candidate = firstSentence.length <= firstTenWords.length ? firstSentence : firstTenWords;
    return candidate.length < t.length ? candidate + "…" : candidate;
}
/**
 * Produce the speech synthesis string for one transcript line.
 *
 * Behaviour contract (pinned by DDT):
 *   - `null` / `undefined` line → returns `""`.
 *   - Both preamble and text empty → returns `""`.
 *   - Preamble only (no text) → returns the trimmed preamble.
 *   - Text only (no preamble) → returns the trimmed text
 *     (after optional skim-trim when mode=`"skim"` and `line.skimmable`).
 *   - Both present:
 *       - preamble ending in `","` or `"."` → `"${preamble} ${text}"` (space only).
 *       - otherwise → `"${preamble}, ${text}"` (comma-space join).
 *   - Skim-trim is applied to `text` when `mode === "skim"` AND
 *     `line.skimmable === true` AND `line.role` is in `SKIMMABLE_ROLES`
 *     (`Paragraph`, `P`, `Note`, `Caption`, `ListItem`, `LI`).
 *
 * @param line - Partial transcript line (preamble, text, role, skimmable).
 * @param mode - `"full"` (no skim-trim) or `"skim"` (apply skimTrim to long text).
 * @returns The speech string (never null).
 */
export function speakableTextForLine(line, mode) {
    if (!line)
        return "";
    const m = mode === "full" ? "full" : "skim";
    const pre = (line.preamble || "").trim();
    let txt = (line.text || "").trim();
    if (m === "skim" && line.skimmable && SKIMMABLE_ROLES.has(line.role || "")) {
        txt = skimTrim(txt);
    }
    if (!pre && !txt)
        return "";
    if (!pre)
        return txt;
    if (!txt)
        return pre;
    return pre.endsWith(",") || pre.endsWith(".") ? `${pre} ${txt}` : `${pre}, ${txt}`;
}
/**
 * Select a preferred English male voice from the browser's voice list.
 *
 * Behaviour contract (pinned by DDT):
 *   - Empty or non-array input → returns `null`.
 *   - Filters to English voices (`lang` starts with `"en"`, case-insensitive).
 *   - Searches the preferred name list in priority order:
 *     `["Daniel", "Tom", "Alex", "Mark", "Microsoft David", "Fred"]`.
 *   - Falls back to a heuristic male-name regex over the English subset.
 *   - Falls back to the first English voice, then the first voice of any language.
 *   - Returns `null` only when the input array is empty.
 *
 * @param voices - Available voices (typically from `SpeechSynthesis.getVoices()`).
 * @returns The best-match voice or `null`.
 */
export function pickMaleVoice(voices) {
    if (!Array.isArray(voices) || voices.length === 0)
        return null;
    const en = voices.filter((v) => v && typeof v.lang === "string" && v.lang.toLowerCase().startsWith("en"));
    const preferred = ["Daniel", "Tom", "Alex", "Mark", "Microsoft David", "Fred"];
    for (const name of preferred) {
        const hit = en.find((v) => typeof v.name === "string" && v.name.includes(name));
        if (hit)
            return hit;
    }
    const malesuspect = en.find((v) => typeof v.name === "string"
        && /\b(male|man|david|tom|james|john|michael|paul|peter|brian)\b/i.test(v.name));
    if (malesuspect)
        return malesuspect;
    return (en[0] ?? voices[0]) ?? null;
}
// ─── DOM controller (browser-only) ───────────────────────────────────────────
const VERBOSE_KEY = "ada-editor-sr-verbose-mode";
const DEFAULT_PITCH = 0.92;
const DEFAULT_RATE = 0.95;
export const SpeechState = Object.freeze({ IDLE: "idle", PLAYING: "playing", PAUSED: "paused" });
export function ScreenReaderPanel(hooks) {
    if (!hooks?.container || typeof hooks.getCards !== "function") {
        throw new Error("ScreenReaderPanel: hooks.container and hooks.getCards required");
    }
    const doc = hooks.container.ownerDocument
        || (typeof document !== "undefined" ? document : null);
    if (!doc)
        throw new Error("ScreenReaderPanel: no document available");
    const observeRoot = hooks.observeRoot ?? hooks.container;
    let isOpen = false;
    let speechState = SpeechState.IDLE;
    let currentIdx = -1;
    let utterances = [];
    let mode = readVerboseMode();
    let pickedVoice = null;
    const HAS_SPEECH = typeof window !== "undefined"
        && (window).speechSynthesis !== undefined
        && window.SpeechSynthesisUtterance !== undefined;
    if (HAS_SPEECH) {
        try {
            pickedVoice = pickMaleVoice(window.speechSynthesis.getVoices());
        }
        catch (_) { /* best-effort before user gesture */ } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        try {
            window.speechSynthesis.addEventListener("voiceschanged", () => {
                pickedVoice = pickMaleVoice(window.speechSynthesis.getVoices()); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            });
        }
        catch (_) { /* best-effort: addEventListener may not exist on some implementations */ }
    }
    const backdrop = doc.createElement("div");
    backdrop.className = "sr-backdrop";
    backdrop.setAttribute("aria-hidden", "true");
    hooks.container.appendChild(backdrop);
    const panel = doc.createElement("aside");
    panel.className = "sr-panel";
    panel.setAttribute("role", "complementary");
    panel.setAttribute("aria-label", "Screen-reader preview");
    if (!HAS_SPEECH)
        panel.classList.add("no-speech");
    const header = doc.createElement("header");
    header.className = "sr-panel-header";
    const titleEl = doc.createElement("span");
    titleEl.className = "sr-panel-title";
    titleEl.textContent = "Screen-reader preview";
    // Migrated to the SHARED builder (rule #11 / A.7 — the SAME "✕" the O365 a11y-view
    // SR popout renders; identical `.sr-close-btn` DOM + WCAG 2.5.3 aria-hidden glyph).
    const closeBtn = buildSrCloseButton(doc);
    header.appendChild(titleEl);
    header.appendChild(closeBtn);
    const controls = doc.createElement("div");
    controls.className = "sr-panel-controls";
    const playBtn = doc.createElement("button");
    playBtn.className = "sr-play-btn editor-btn";
    playBtn.setAttribute("aria-label", "Play / Pause");
    const playIcon = doc.createElement("span");
    playIcon.className = "sr-play-icon";
    // Decorative glyph (WCAG 2.5.3): the visible text label is the sibling
    // <span> "Play"; the icon must be aria-hidden so it isn't counted as part
    // of the visible label matched against the aria-label.
    playIcon.setAttribute("aria-hidden", "true");
    playIcon.textContent = "▶";
    const playLabel = doc.createElement("span");
    playLabel.className = "sr-play-label";
    playLabel.textContent = "Play";
    playBtn.appendChild(playIcon);
    playBtn.appendChild(playLabel);
    const stopBtn = doc.createElement("button");
    stopBtn.className = "sr-stop-btn editor-btn";
    stopBtn.setAttribute("aria-label", "Stop");
    stopBtn.disabled = true;
    const stopIcon = doc.createElement("span");
    stopIcon.className = "sr-stop-icon";
    stopIcon.setAttribute("aria-hidden", "true"); // decorative glyph — WCAG 2.5.3, sibling label "Stop" is the visible name
    stopIcon.textContent = "■";
    const stopLabel = doc.createElement("span");
    stopLabel.className = "sr-stop-label";
    stopLabel.textContent = "Stop";
    stopBtn.appendChild(stopIcon);
    stopBtn.appendChild(stopLabel);
    const verboseBtn = doc.createElement("button");
    verboseBtn.className = "sr-verbose-btn editor-btn";
    verboseBtn.setAttribute("aria-label", "Toggle skim/full");
    const vIcon = doc.createElement("span");
    vIcon.className = "sr-verbose-icon";
    vIcon.setAttribute("aria-hidden", "true"); // decorative glyph — WCAG 2.5.3, sibling label "Skim"/"Full" is the visible name
    vIcon.textContent = "⚡";
    const vLabel = doc.createElement("span");
    vLabel.className = "sr-verbose-label";
    vLabel.textContent = "Skim";
    verboseBtn.appendChild(vIcon);
    verboseBtn.appendChild(vLabel);
    controls.appendChild(playBtn);
    controls.appendChild(stopBtn);
    controls.appendChild(verboseBtn);
    const statusEl = doc.createElement("div");
    statusEl.className = "sr-panel-status";
    statusEl.setAttribute("role", "status");
    statusEl.setAttribute("aria-live", "polite");
    const transcriptEl = doc.createElement("ol");
    transcriptEl.className = "sr-transcript";
    transcriptEl.setAttribute("role", "list");
    transcriptEl.setAttribute("aria-label", "Linearized transcript");
    panel.appendChild(header);
    panel.appendChild(controls);
    panel.appendChild(statusEl);
    panel.appendChild(transcriptEl);
    hooks.container.appendChild(panel);
    applyVerboseButton();
    let mo = null;
    if (typeof MutationObserver !== "undefined" && observeRoot) {
        mo = new MutationObserver(() => { if (isOpen)
            rebuild(); });
        try {
            mo.observe(observeRoot, {
                childList: true, subtree: true, attributes: true,
                attributeFilter: ["data-type", "data-role", "data-alt"],
            });
        }
        catch (_) { /* observeRoot may be a stub element in tests */ }
    }
    const onInput = (ev) => {
        if (!isOpen)
            return;
        const t = ev?.target;
        if (t?.matches && (t.matches("input") || t.matches("textarea"))) {
            if (transcriptEl.querySelector(".sr-text.is-editing"))
                return;
            rebuild();
        }
    };
    if (observeRoot && typeof observeRoot.addEventListener === "function") {
        observeRoot.addEventListener("input", onInput);
    }
    closeBtn.addEventListener("click", close);
    playBtn.addEventListener("click", playPause);
    stopBtn.addEventListener("click", stop);
    verboseBtn.addEventListener("click", toggleVerbose);
    if (hooks.toggleButton && typeof hooks.toggleButton.addEventListener === "function") {
        hooks.toggleButton.addEventListener("click", () => (isOpen ? close() : open()));
    }
    const onDocKey = (ev) => {
        if (ev.key === "Escape" && isOpen) {
            ev.stopPropagation();
            close();
        }
    };
    if (doc && typeof doc.addEventListener === "function") {
        doc.addEventListener("keydown", onDocKey);
    }
    // Shared Tab-cycle trap (#14): this panel had Escape-close (onDocKey) but no focus
    // trap, so Tab could walk out into the pdf.js document behind it. Adopt the trap
    // primitives — a pure ADD; the existing Escape handling stays as-is.
    const panelTrap = new FocusTrap();
    function open() {
        isOpen = true;
        backdrop.classList.add("is-open");
        panel.classList.add("is-open");
        panelTrap.trap(panel);
        if (hooks.toggleButton?.classList) {
            hooks.toggleButton.classList.add("is-active");
            hooks.toggleButton.setAttribute("aria-pressed", "true");
        }
        rebuild();
    }
    function close() {
        stop();
        isOpen = false;
        backdrop.classList.remove("is-open");
        panel.classList.remove("is-open");
        panelTrap.release();
        if (hooks.toggleButton?.classList) {
            hooks.toggleButton.classList.remove("is-active");
            hooks.toggleButton.setAttribute("aria-pressed", "false");
        }
    }
    function rebuild() {
        if (!isOpen)
            return;
        if (transcriptEl.querySelector(".sr-text.is-editing"))
            return;
        const cards = hooks.getCards() || [];
        const lines = buildTranscript(cards);
        transcriptEl.innerHTML = "";
        lines.forEach((info, idx) => {
            const li = doc.createElement("li");
            li.className = "sr-line"
                + (info.kind === "undef" ? " sr-line-undef" : "")
                + (info.kind === "li" ? " sr-line-li" : "");
            li.dataset.order = String(idx + 1);
            li.dataset.structId = info.structId || "";
            li.dataset.role = info.role || "";
            li.setAttribute("tabindex", "0");
            if (info.preamble) {
                const pre = doc.createElement("span");
                pre.className = "sr-pre"
                    + (info.kind === "undef" ? " sr-pre-warning" : "")
                    + (info.kind === "empty" ? " sr-pre-error" : "");
                pre.textContent = info.preamble + (info.text ? "," : "");
                li.appendChild(pre);
                const sp = doc.createElement("span");
                sp.className = "sr-spacer";
                sp.textContent = " ";
                li.appendChild(sp);
            }
            if (info.text) {
                const tx = doc.createElement("span");
                tx.className = "sr-text"
                    + (info.kind === "empty" ? " sr-text-empty" : "")
                    + (info.editable ? " sr-editable" : "");
                tx.textContent = info.text;
                if (info.editable) {
                    if (info.isPlaceholder)
                        tx.dataset.placeholder = "1";
                    tx.title = "Click to edit description";
                    tx.addEventListener("click", (ev) => {
                        ev.stopPropagation();
                        openInlineEditor(tx, info, idx);
                    });
                }
                li.appendChild(tx);
            }
            li.addEventListener("click", (ev) => {
                const t = ev.target;
                if (t?.classList?.contains("sr-text") && t.classList.contains("sr-editable"))
                    return;
                onLineClicked(info, idx, li);
            });
            if (info.structId && typeof hooks.getCardEl === "function") {
                li.addEventListener("mouseenter", () => {
                    const cardEl = hooks.getCardEl(info.structId);
                    cardEl?.classList.add("sr-hover-mirror");
                });
                li.addEventListener("mouseleave", () => {
                    const cardEl = hooks.getCardEl(info.structId);
                    cardEl?.classList.remove("sr-hover-mirror");
                });
            }
            transcriptEl.appendChild(li);
        });
    }
    function onLineClicked(info, idx, lineEl) {
        flash(lineEl, "flash-card");
        if (typeof hooks.getCardEl === "function") {
            const cardEl = hooks.getCardEl(info.structId);
            if (cardEl) {
                flash(cardEl, "flash-card");
                if (typeof cardEl.scrollIntoView === "function") {
                    cardEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
            }
        }
        if (typeof hooks.getOverlayEl === "function") {
            const ovEl = hooks.getOverlayEl(info.structId);
            if (ovEl)
                flash(ovEl, "flash-ov");
        }
        if (HAS_SPEECH && speechState !== SpeechState.IDLE && typeof idx === "number") {
            playFrom(idx);
        }
    }
    function flash(el, cls) {
        if (!el?.classList)
            return;
        el.classList.add(cls);
        setTimeout(() => { try {
            el.classList.remove(cls);
        }
        catch (_) { } }, 600);
    }
    function openInlineEditor(span, info, _idx) {
        if (span.classList.contains("is-editing"))
            return;
        const wasPlaceholder = span.dataset.placeholder === "1";
        const startVal = wasPlaceholder ? "" : (info.text || "");
        span.textContent = startVal;
        span.classList.add("is-editing");
        span.classList.remove("sr-text-empty");
        span.contentEditable = "true";
        try {
            span.focus();
        }
        catch (_) { }
        const commit = () => {
            const newVal = (span.textContent || "").trim();
            span.contentEditable = "false";
            span.classList.remove("is-editing");
            if (typeof hooks.onAltEdit === "function" && newVal !== startVal) {
                // WHY: onAltEdit is fire-and-forget; swallow defensively so a transient
                // hook failure doesn't abort commit() — rebuild() re-paints the transcript.
                try {
                    hooks.onAltEdit(info.structId, newVal);
                }
                catch (_) { /* see comment */ }
            }
            rebuild();
        };
        const cancel = () => {
            span.contentEditable = "false";
            span.classList.remove("is-editing");
            rebuild();
        };
        span.addEventListener("blur", commit, { once: true });
        span.addEventListener("keydown", function onKey(ev) {
            if ((ev).key === "Enter" && !(ev).shiftKey) {
                ev.preventDefault();
                span.removeEventListener("keydown", onKey);
                try {
                    span.blur();
                }
                catch (_) {
                    commit();
                }
            }
            else if ((ev).key === "Escape") {
                ev.preventDefault();
                span.removeEventListener("blur", commit);
                span.removeEventListener("keydown", onKey);
                cancel();
            }
        });
    }
    function setSpeechState(next) {
        speechState = next;
        const isPlaying = next === SpeechState.PLAYING;
        const isPaused = next === SpeechState.PAUSED;
        playBtn.classList.toggle("is-playing", isPlaying);
        playIcon.textContent = isPlaying ? "⏸" : "▶";
        playLabel.textContent = isPaused ? "Resume" : (isPlaying ? "Pause" : "Play");
        stopBtn.disabled = (next === SpeechState.IDLE);
        if (next === SpeechState.PAUSED)
            statusEl.textContent = "Paused";
        else if (next === SpeechState.IDLE)
            statusEl.textContent = "";
    }
    function playPause() {
        if (!HAS_SPEECH)
            return;
        if (speechState === SpeechState.PLAYING) {
            try {
                window.speechSynthesis.pause();
            }
            catch (_) { } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            setSpeechState(SpeechState.PAUSED);
            return;
        }
        if (speechState === SpeechState.PAUSED) {
            try {
                window.speechSynthesis.resume();
            }
            catch (_) { } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            setSpeechState(SpeechState.PLAYING);
            statusEl.textContent = "Reading…";
            return;
        }
        playFrom(0);
    }
    function playFrom(startIdx) {
        if (!HAS_SPEECH)
            return;
        try {
            window.speechSynthesis.cancel();
        }
        catch (_) { } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        setTimeout(() => queueFrom(startIdx), 50);
    }
    function queueFrom(startIdx) {
        utterances = [];
        currentIdx = -1;
        if (!pickedVoice) {
            try {
                pickedVoice = pickMaleVoice(window.speechSynthesis.getVoices());
            }
            catch (_) { } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        }
        const lineEls = transcriptEl.querySelectorAll(".sr-line");
        const cards = hooks.getCards() || [];
        const lines = buildTranscript(cards);
        for (let idx = 0; idx < lines.length; idx++) {
            if (idx < startIdx)
                continue;
            const text = speakableTextForLine(lines[idx], mode);
            if (!text)
                continue;
            const u = new window.SpeechSynthesisUtterance(text); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            u.pitch = DEFAULT_PITCH;
            u.rate = DEFAULT_RATE;
            if (pickedVoice)
                u.voice = pickedVoice;
            const lineEl = lineEls[idx];
            u.onstart = () => {
                lineEls.forEach((l) => l.classList?.remove("is-now-speaking"));
                if (lineEl?.classList)
                    lineEl.classList.add("is-now-speaking");
                if (lineEl && typeof lineEl.scrollIntoView === "function") {
                    lineEl.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                currentIdx = idx;
            };
            u.onend = () => {
                if (lineEl?.classList)
                    lineEl.classList.remove("is-now-speaking");
                if (idx === lines.length - 1) {
                    setSpeechState(SpeechState.IDLE);
                    statusEl.textContent = "Done.";
                }
            };
            utterances.push(u);
            try {
                window.speechSynthesis.speak(u);
            }
            catch (_) { } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        }
        setSpeechState(SpeechState.PLAYING);
        statusEl.textContent = startIdx === 0 ? "Reading…" : `Resuming from line ${startIdx + 1}…`;
    }
    function stop() {
        if (!HAS_SPEECH)
            return;
        try {
            window.speechSynthesis.cancel();
        }
        catch (_) { } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        setSpeechState(SpeechState.IDLE);
        for (const l of transcriptEl.querySelectorAll(".sr-line.is-now-speaking")) {
            l.classList.remove("is-now-speaking");
        }
    }
    function toggleVerbose() {
        mode = (mode === "full") ? "skim" : "full";
        try {
            localStorage.setItem(VERBOSE_KEY, mode);
        }
        catch (_) { } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        applyVerboseButton();
    }
    function applyVerboseButton() {
        const isSkim = mode === "skim";
        verboseBtn.classList.toggle("is-skim", isSkim);
        vLabel.textContent = isSkim ? "Skim" : "Full";
        vIcon.textContent = isSkim ? "⚡" : "📖";
        verboseBtn.title = isSkim ? "Skim mode (click for Full)" : "Full mode (click for Skim)";
        verboseBtn.setAttribute("aria-pressed", isSkim ? "true" : "false");
    }
    function destroy() {
        try {
            stop();
        }
        catch (_) { }
        try {
            panelTrap.release();
        }
        catch (_) { }
        try {
            mo?.disconnect();
        }
        catch (_) { }
        if (observeRoot && typeof observeRoot.removeEventListener === "function") {
            observeRoot.removeEventListener("input", onInput);
        }
        if (doc && typeof doc.removeEventListener === "function") {
            doc.removeEventListener("keydown", onDocKey);
        }
        try {
            backdrop.remove();
        }
        catch (_) { }
        try {
            panel.remove();
        }
        catch (_) { }
    }
    function _state() {
        return {
            isOpen,
            speechState,
            isSpeaking: speechState !== SpeechState.IDLE,
            isPaused: speechState === SpeechState.PAUSED,
            mode,
            currentIdx,
            voiceName: pickedVoice ? pickedVoice.name : null,
        };
    }
    return { open, close, isOpen: () => isOpen, rebuild, destroy, _state };
}
// ─── Verbosity-mode persistence ──────────────────────────────────────────────
function readVerboseMode() {
    try {
        const v = (typeof localStorage !== "undefined") ? localStorage.getItem(VERBOSE_KEY) : null; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (v === "full" || v === "skim")
            return v;
    }
    catch (_) { /* localStorage may be unavailable in sandboxed contexts */ }
    return "skim";
}
export const __test__ = { readVerboseMode, VERBOSE_KEY, SKIMMABLE_ROLES, DESC_ROLES };
